import {moveTo, tick, reset, addPlane, removePlane} from "./planeSlice.js";
import {selectPlane, CONDITION_TYPES} from "./planeSlice.js";
import store from "./store.js";

import Refuel from "./actions/Refuel.js";
import WaitDepartureTime from "./actions/WaitDepartureTime.js";
import WaitAirportOpen from "./actions/WaitAirportOpen.js";
import WaitFuelOpen from "./actions/WaitFuelOpen.js";
import WaitFuelDelivery from "./actions/WaitFuelDelivery.js";
import LineUp from "./actions/LineUp.js";
import TakeOffRoll from "./actions/TakeOffRoll.js";
import Climb from "./actions/Climb.js";
import Descend from "./actions/Descend.js";
import TurnDegrees from "./actions/TurnDegrees.js";
import FlyDirect from "./actions/FlyDirect.js";
import FlyDirectDescend from "./actions/FlyDirectDescend.js";
import FlyDirectClimb from "./actions/FlyDirectClimb.js";
import InterceptTrack from "./actions/InterceptTrack.js";
import ContinueWhenReached from "./actions/ContinueWhenReached.js";
import JoinDownwind from "./actions/JoinDownwind.js";
import FlyDownwind from "./actions/FlyDownwind.js";
import JoinFinal from "./actions/JoinFinal.js";
import FlyFinal from "./actions/FlyFinal.js";
import Land from "./actions/Land.js";

import {getAirportDetails} from "../assets/virtual-flyout/airport-service.js";
import {FUEL_TYPES} from "../assets/virtual-flyout/perf-calc.js";
class PlaneController {
    TAKEOFF_MIN_ALT = 400;
    FINAL_ALT = 400;

    constructor({planeId, type, pob, onEvent, onEnd}) {
        this.planeId = planeId;
        this.type = type;
        this.pob = pob;
        this.onEnd = onEnd;
        this.onEvent = onEvent;
        this.actionQueue = [];
        this.currentAction = -1;
        store.dispatch(addPlane({ planeId, type, pob }));
    }

    /* Actions */
    moveTo(airport) {
        const airportDetails = getAirportDetails(airport.code);
        store.dispatch(moveTo({
            planeId: this.planeId,
            coordinates: {
                lat: airport.lat,
                long: airport.long
            },
            altitude: airportDetails.elevation
        }));
    }

    refuel(airport, fuelQty) {
        const fuelType = FUEL_TYPES[this.type];
        this.addToQueue(new WaitFuelOpen(this.planeId, airport));
        this.addToQueue(new WaitFuelDelivery(this.planeId, airport));
        this.addToQueue(new Refuel(this.planeId, airport, fuelType, fuelQty));
    }

    takeOff(airport, runway=null, departureTime=null) {
        const airportDetails = getAirportDetails(airport.code);
        if (departureTime) {
            this.addToQueue(new WaitDepartureTime(this.planeId, departureTime));
        }
        this.addToQueue(new WaitAirportOpen(this.planeId, airport));
        this.addToQueue(new LineUp(this.planeId, airport, runway));
        this.addToQueue(new TakeOffRoll(this.planeId, airport, runway));
        this.addToQueue(new Climb(this.planeId, airportDetails.elevation + this.TAKEOFF_MIN_ALT));
        this.addToQueue(new ContinueWhenReached(this.planeId));
    }

    turnDegrees(degrees) {
        this.addToQueue(new TurnDegrees(this.planeId, degrees));
    }

    flyDirect(waypoint) {
        this.addToQueue(new FlyDirect(this.planeId, waypoint));
    }

    flyDirectDescend(waypoint, altitude) {
        this.addToQueue(new FlyDirectDescend(this.planeId, waypoint, altitude));
    }

    flyDirectClimb(waypoint, altitude) {
        this.addToQueue(new FlyDirectClimb(this.planeId, waypoint, altitude));
    }

    interceptTrack(waypoint1, waypoint2) {
        this.addToQueue(new InterceptTrack(this.planeId, waypoint1, waypoint2));
    }

    climb(altitude) {
        this.addToQueue(new Climb(this.planeId, altitude));
    }

    descend(altitude) {
        this.addToQueue(new Descend(this.planeId, altitude));
    }

    continueWhenReached() {
        this.addToQueue(new ContinueWhenReached(this.planeId));
    }

    visualCircuit(airport) {
        this.addToQueue(new JoinDownwind(this.planeId, airport));
        this.addToQueue(new FlyDownwind(this.planeId, airport));
        this.addToQueue(new JoinFinal(this.planeId, airport));
        this.addToQueue(new FlyFinal(this.planeId, airport));
        this.addToQueue(new Land(this.planeId, airport));
    }

    /* Queue management */
    addToQueue(action) {
        this.actionQueue.push(action);
    }

    moreActions() {
        return this.currentAction < this.actionQueue.length - 1;
    }

    isActionInProgress() {
        return this.currentAction !== -1 && !this.actionQueue[this.currentAction].isComplete();
    }

    executeNextAction() {
        this.currentAction += 1;
        const action = this.actionQueue[this.currentAction];
        action.execute();
    }

    tick(timePassed) {
        store.dispatch(tick({ planeId: this.planeId, timePassed }));
        while (!this.isActionInProgress()) {
            if (this.moreActions()) {
                this.executeNextAction();
            } else {
                this.onEnd();
                return;
            }
        }
        // check plane conditions
        const planeState = selectPlane(store.getState(), this.planeId);
        if (planeState.conditions.length > 0) {
            for (let condition of planeState.conditions) {
                if (condition === CONDITION_TYPES.OUT_OF_FUEL) {
                    this.onEvent("Out of fuel");
                    if (!planeState.airborn) {
                        this.onEnd();
                    }
                } else if (condition === CONDITION_TYPES.MTOW_EXCEEDED) {
                    this.onEvent("MTOW exceeded");
                    this.onEnd();
                } else if (condition === CONDITION_TYPES.TORR_EXCEEDED) {
                    this.onEvent("TORR exceeded");
                    this.onEnd();
                } else if (condition === CONDITION_TYPES.TODR_EXCEEDED) {
                    this.onEvent("TODR exceeded");
                    this.onEnd();
                } else if (condition === CONDITION_TYPES.LDR_EXCEEDED) {
                    this.onEvent("LDR exceeded");
                    this.onEnd();
                }
            }
        }
    }

    reset() {
        store.dispatch(reset({ planeId: this.planeId, type: this.type, pob: this.pob }));
    }
}

export const EVENT_TYPES = {
    CAUTION: "CAUTION",
    WARNING: "WARNING",
    INFO: "INFO"
}

export default PlaneController;